import { computed, effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { DefaultLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { FirstTouchOriginator } from '@app/+onboarding/onboarding.model';
import { OnboardingStore } from '@app/+onboarding/onboarding.store';
import { OnboardingUtils } from '@app/+onboarding/onboarding.utils';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { AppState } from '@app/reducers/interfaces';
import { getEmployeesCount } from '@app/reducers/orm/employee/employee.selectors';
import { UserGuideStatus, UserGuideType } from '@app/reducers/orm/user-guide/user-guide.model';
import { isUserGuideLoaded, selectUserGuideByType } from '@app/reducers/orm/user-guide/user-guide.selector';
import { UserGuideService } from '@app/reducers/orm/user-guide/user-guide.service';
import { SmbHospitalityOnboardingChecklistStep } from '@app/shared/onboarding/onboarding-checklist.model';
import { OnboardingChecklistService } from '@app/shared/onboarding/onboarding-checklist.service';
import { watchState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, of, take, tap } from 'rxjs';

import {
  FIRST_TOUCH_MODULES,
  FIRST_TOUCH_MODULES_SUPPORTED_LANGUAGES,
  FirstTouchModule,
  FirstTouchModuleConfig,
  getFirstTouchModuleForStep,
} from './first-touch.model';

@Injectable({
  providedIn: 'root',
})
export class FirstTouchService {
  private router = inject(Router);
  private store = inject<Store<AppState>>(Store);
  private userGuideService = inject(UserGuideService);
  private translateService = inject(TranslateService);
  private onboardingChecklistService = inject(OnboardingChecklistService);
  private onboardingUtils = inject(OnboardingUtils);
  private onboardingStore = inject(OnboardingStore);

  private userGuideType = UserGuideType.TOUCHED_MODULES;
  private userGuide = this.store.selectSignal(selectUserGuideByType(this.userGuideType));
  private isUserGuideLoaded = this.store.selectSignal(isUserGuideLoaded);
  private employeesCount = this.store.selectSignal(getEmployeesCount);
  private touchedModules = computed(() => {
    const userGuide = this.userGuide();
    if (!userGuide) {
      return [];
    }
    return this.userGuideService.getListOfSteps(userGuide.step);
  });
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
  );
  private permissionState = this.store.selectSignal(getPermissionState);

  activeFirstTouchModule = computed<FirstTouchModule | undefined>(() => {
    const currentUrl = this.currentUrl();

    return Object.entries(FIRST_TOUCH_MODULES).find(([, config]) => config.urlRegExp?.test(currentUrl))?.[0] as
      FirstTouchModule | undefined;
  });
  firstTouchConfig = computed<FirstTouchModuleConfig | undefined>(() => {
    const activeModule = this.activeFirstTouchModule();

    if (!activeModule) {
      return undefined;
    }

    const config = FIRST_TOUCH_MODULES[activeModule];

    let langCodeSuffix = '';
    if (config.illustrationIsLocalized) {
      let langCode = (this.translateService.getCurrentLang() ?? DefaultLocale).slice(0, 2);
      if (!FIRST_TOUCH_MODULES_SUPPORTED_LANGUAGES.includes(langCode)) {
        langCode = 'en'; // Fallback to English if the language is not supported
      }
      langCodeSuffix = `_${langCode}`;
    }

    const ctaRights = this.ctaRightsCheck(activeModule);
    if (!ctaRights) {
      config.buttonLabel = config.fallbackButtonLabel;
    }

    return {
      ...config,
      illustrationPath: `assets/images/first-touch/${activeModule.toLocaleLowerCase()}${langCodeSuffix}.png`,
    };
  });
  private canDisplayFirstTouch = computed(() => {
    if (!this.isUserGuideLoaded()) {
      return false;
    }

    const isAccountManager = this.permissionState()?.isAccountManager;
    if (!isAccountManager) {
      return false;
    }

    const activeModule = this.activeFirstTouchModule();
    if (!activeModule) {
      return false;
    }

    const touchedModules = this.touchedModules();
    if (touchedModules.includes(activeModule)) {
      return false;
    }

    return true;
  });
  private firstTouchShouldBeShown = computed(() => {
    const activeModule = this.activeFirstTouchModule();
    return !!activeModule && this.shouldFirstTouchStateBeShown(activeModule);
  });
  shouldShowFirstTouch = computed(() => this.canDisplayFirstTouch() && this.firstTouchShouldBeShown());

  onFirstTouchButtonClick() {
    const activeModule = this.activeFirstTouchModule();
    if (activeModule) {
      return this.markModuleAsTouched(activeModule).pipe(
        take(1),
        tap(() => {
          this.handleFirstTouchButtonClick(activeModule);
        }),
      );
    }
    return of(undefined);
  }

  // TODO seemingly no reason why this should return an observable.
  private markModuleAsTouched(module: FirstTouchModule) {
    let touchedModules = this.touchedModules();
    if (touchedModules.includes(module)) {
      return of(undefined);
    }

    touchedModules = [...touchedModules, module];

    return this.userGuideService.trackUserGuide({
      guide: this.userGuideType,
      step: this.userGuideService.mergeListOfSteps(touchedModules),
      status: UserGuideStatus.IN_PROGRESS,
    });
  }

  private handleFirstTouchButtonClick(module: FirstTouchModule) {
    switch (module) {
      case FirstTouchModule.NEWS:
        this.router.navigate(['', { outlets: { modal: ['news-item'] } }]);
        break;
      case FirstTouchModule.SCHEDULE:
        this.onboardingStore.updateState(SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT, {
          originator: FirstTouchOriginator,
        });
        break;
      case FirstTouchModule.TIMESHEET:
        if (!this.onboardingUtils.canAccessStep(SmbHospitalityOnboardingChecklistStep.TIME_TRACKING)) {
          this.router.navigate(['', { outlets: { modal: ['timesheet'] } }]);
          return;
        }
        this.onboardingStore.updateState(SmbHospitalityOnboardingChecklistStep.TIME_TRACKING, {
          originator: FirstTouchOriginator,
        });
        break;
      case FirstTouchModule.EMPLOYEES:
        this.router.navigate(['', { outlets: { modal: ['quick-user-import'] } }]);
        break;
    }
  }

  private shouldFirstTouchStateBeShown(module: FirstTouchModule): boolean {
    switch (module) {
      case FirstTouchModule.SCHEDULE:
        return !this.onboardingChecklistService.wasStepEverCompleted(
          SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT,
        );
      case FirstTouchModule.TIMESHEET:
        return !this.onboardingChecklistService.wasStepEverCompleted(
          SmbHospitalityOnboardingChecklistStep.TIME_TRACKING,
        );
      case FirstTouchModule.EMPLOYEES:
        return this.employeesCount() < 2;
      default:
        return true;
    }
  }

  private ctaRightsCheck(module: FirstTouchModule) {
    switch (module) {
      case FirstTouchModule.TIMESHEET:
        return this.onboardingUtils.canAccessStep(SmbHospitalityOnboardingChecklistStep.TIME_TRACKING);
      default:
        return true;
    }
  }

  constructor() {
    watchState(this.onboardingStore, ({ step, config }) => {
      if (!step) return;
      const firstTouchModule = getFirstTouchModuleForStep(step);

      if (config?.originator !== FirstTouchOriginator && firstTouchModule) {
        void this.markModuleAsTouched(firstTouchModule).pipe(take(1)).subscribe();
      }
    });

    effect(() => {
      const activeModule = this.activeFirstTouchModule();
      if (activeModule && this.canDisplayFirstTouch() && !this.firstTouchShouldBeShown()) {
        this.markModuleAsTouched(activeModule).pipe(take(1)).subscribe();
      }
    });
  }
}
